import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import TaskManagement from "../employee/TaskManagement";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import InventoryTodo from "../inventory/InventoryTodo";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const PAGE_META = {
  all: {
    title: "Task Management",
    subtitle: "Assign and manage field tasks",
  },
  create: {
    title: "Task Management",
    subtitle: "Assign and manage field tasks",
  },
  inventory: {
    title: "Inventory Management",
    subtitle: "Manage inventory items, stock and transactions",
  },
};

export default function TasksModulePage({ section = "all" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);

  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const meta = PAGE_META[section] || PAGE_META.all;

  const sidebarItems = useGlobalSidebarItems();

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <Box>
          <Typography sx={{ mt: 0.2, color: "#7c8ba1", fontSize: 13.5 }}>
            Tasks / {section === "inventory" ? "Inventory" : section === "create" ? "Create Task" : "All Tasks"}
          </Typography>
          <Typography sx={{ mt: 1.1, fontSize: { xs: "2rem", md: "2.35rem" }, fontWeight: 900, color: "#0f172a" }}>
            {meta.title}
          </Typography>
          <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15.5 }}>
            {meta.subtitle}
          </Typography>
        </Box>

        {section === "inventory" ? (
          <InventoryTodo embedded />
        ) : (
          <TaskManagement
            forcedView={section === "create" ? "create" : "list"}
            hideShell
            embedded
            onNavigateToCreate={() => navigate("/tasks/create")}
            onNavigateToList={() => navigate("/tasks/all")}
          />
        )}
      </main>
    </div>
  );
}
